import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Anchor, Box, Group, Stack } from '@mantine/core'
import { AppButton, AppSkeleton, AppText } from '../../app/common/ui'
import { format, isToday, isThisWeek } from 'date-fns'
import { useStore } from '../../app/stores/store'
import { EmptyState } from '../../app/common/ui'
import type { NotificationItem, NotificationType } from '../../app/stores/notificationStore'
import AppealComingSoonModal from '../settings/AppealComingSoonModal'
import agent from '../../app/api/agent'
import { toast } from 'react-toastify'

const TYPE_COLORS: Record<NotificationType | string, string> = {
  NewFollower: '#58A6FF',
  PostUpvoted: '#E85D04',
  CommentOnPost: '#3FB950',
  NewComment: '#3FB950',
  VoteMilestone: '#C9A84C',
  WishlistAdd: '#58A6FF',
  PartApproved: '#3FB950',
  PartRejected: '#F85149',
  ModelApproved: '#3FB950',
  ModelRejected: '#F85149',
  FlagResolved: '#D29922',
  ContentModerated: '#D29922',
  FlagResolved_Warned: '#D29922',
  FlagResolved_Removed: '#F85149',
  FlagResolved_Suspended: '#F85149',
  ReputationChange: '#C9A84C',
  BuildCollaboratorInvited: '#58A6FF',
  BuildCollaboratorAccepted: '#3FB950'
}

function isModerationNotification (type?: string): boolean {
  return type === 'ContentModerated' ||
    type === 'FlagResolved_Warned' ||
    type === 'FlagResolved_Removed' ||
    type === 'FlagResolved_Suspended'
}

function parsePolicyPath (message: string): string | null {
  const match = message.match(/See (\/(?:terms|guidelines|privacy)(?:[^\s.)]*)?)/i)
  return match?.[1] ?? null
}

const MESSAGE_LINE_SX = {
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 12,
  lineHeight: 1.45,
  color: 'var(--gs-text)',
  wordBreak: 'break-word' as const
}

const BTN_STYLE = {
  background: '#21262D',
  border: '1.5px solid #484F58',
  color: '#E6EDF3',
  padding: '6px 14px',
  borderRadius: 5,
  fontSize: 12,
  fontFamily: 'DM Sans, sans-serif',
  cursor: 'pointer' as const
}

function getSection (date: Date): 'TODAY' | 'THIS WEEK' | 'EARLIER' {
  if (isToday(date)) return 'TODAY'
  if (isThisWeek(date, { weekStartsOn: 0 })) return 'THIS WEEK'
  return 'EARLIER'
}

function groupBySection (items: NotificationItem[]): { section: 'TODAY' | 'THIS WEEK' | 'EARLIER'; items: NotificationItem[] }[] {
  const groups: Record<string, NotificationItem[]> = { TODAY: [], 'THIS WEEK': [], EARLIER: [] }
  const order: ('TODAY' | 'THIS WEEK' | 'EARLIER')[] = ['TODAY', 'THIS WEEK', 'EARLIER']
  items.forEach((n) => {
    const section = getSection(new Date(n.createdAt))
    groups[section].push(n)
  })
  return order.filter((s) => groups[s].length > 0).map((section) => ({ section, items: groups[section] }))
}

function parseCollaboratorInviteTarget (targetId?: string): { buildId: string; collaboratorId: string } | null {
  if (!targetId?.includes('|')) return null
  const [buildId, collaboratorId] = targetId.split('|')
  if (!buildId || !collaboratorId) return null
  return { buildId, collaboratorId }
}

function getNavigatePath (item: NotificationItem): string | null {
  const t = item.targetType
  const id = item.targetId
  const username = item.actorUsername
  if (t === 'Post' && id) return `/blog/${id}`
  if (t === 'SlopPost' && id) return `/slop-feed/${id}`
  if (t === 'Part') return '/parts-bin'
  if (t === 'FirearmModel' && id) return `/registry/${id}`
  if (t === 'Build' && id) return `/builds/${id}`
  if (t === 'BuildCollaboratorInvite' && id) {
    const parsed = parseCollaboratorInviteTarget(id)
    if (parsed) return `/builds/${parsed.buildId}`
  }
  if (t === 'Follow' && username) return `/profiles/${username}`
  return null
}

function isBuildCollaboratorInvite (type?: string) {
  return type === 'BuildCollaboratorInvited'
}

export default observer(function NotificationsPage () {
  const { notificationStore } = useStore()
  const history = useHistory()
  const [appealOpen, setAppealOpen] = useState(false)
  const [appealSummary, setAppealSummary] = useState<string | undefined>()
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null)

  const handleAcceptInvite = async (item: NotificationItem, e: React.MouseEvent) => {
    e.stopPropagation()
    const parsed = parseCollaboratorInviteTarget(item.targetId)
    if (!parsed) return
    setInviteBusyId(item.id)
    try {
      await agent.Builds.acceptCollaborator(parsed.buildId, parsed.collaboratorId)
      toast.success('Invite accepted')
      notificationStore.markRead([item.id])
      const path = `/builds/${parsed.buildId}`
      history.push(path)
    } catch {
      /* interceptor */
    } finally {
      setInviteBusyId(null)
    }
  }

  const handleDeclineInvite = async (item: NotificationItem, e: React.MouseEvent) => {
    e.stopPropagation()
    const parsed = parseCollaboratorInviteTarget(item.targetId)
    if (!parsed) return
    setInviteBusyId(item.id)
    try {
      await agent.Builds.removeCollaborator(parsed.buildId, parsed.collaboratorId)
      toast.info('Invite declined')
      notificationStore.markRead([item.id])
      await notificationStore.loadNotifications({ pageNumber: 1, pageSize: 20 })
    } catch {
      /* interceptor */
    } finally {
      setInviteBusyId(null)
    }
  }

  useEffect(() => {
    notificationStore.loadNotifications({ pageNumber: 1, pageSize: 20 })
  }, [notificationStore])

  const handleItemClick = (item: NotificationItem) => {
    notificationStore.markRead([item.id])
    const path = getNavigatePath(item)
    if (path) history.push(path)
  }

  const grouped = groupBySection(notificationStore.notifications)
  const isEmpty = notificationStore.notifications.length === 0
  const showInitialLoading = notificationStore.loading && isEmpty

  return (
    <Box
      sx={{
        maxWidth: 800,
        margin: '0 auto',
        padding: '16px 24px'
      }}
    >
      <Group position="apart" mb="md">
        <Group spacing="sm">
          <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 20, color: 'var(--gs-text)' }}>
            NOTIFICATIONS
          </AppText>
          {notificationStore.unreadCount > 0 && (
            <Box
              sx={{
                background: 'var(--gs-red)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: 12,
                minWidth: 20,
                textAlign: 'center'
              }}
            >
              {notificationStore.unreadCount > 99 ? '99+' : notificationStore.unreadCount}
            </Box>
          )}
        </Group>
        <Box
          component="button"
          type="button"
          onClick={() => notificationStore.markAllRead()}
          sx={BTN_STYLE}
        >
          Mark all read
        </Box>
      </Group>

      {showInitialLoading
        ? (
        <Stack spacing="sm" mt="md">
          {[1, 2, 3, 4, 5].map((k) => (
            <Group key={k} spacing="md" noWrap align="flex-start">
              <AppSkeleton height={36} width={36} circle />
              <Box sx={{ flex: 1 }}>
                <AppSkeleton height={12} mb={8} />
                <AppSkeleton height={10} width="40%" />
              </Box>
            </Group>
          ))}
        </Stack>
          )
        : isEmpty
          ? (
        <EmptyState
          title="No notifications yet"
          message="Follow users and engage with content to see activity here."
        />
            )
          : (
        <>
          {grouped.map(({ section, items }) => (
            <Box key={section}>
              <AppText
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  fontSize: 10,
                  color: '#8B949E',
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  margin: '14px 0 8px'
                }}
              >
                {section}
              </AppText>
              {items.map((item) => {
                const bgColor = TYPE_COLORS[item.type ?? ''] ?? '#8B949E'
                const moderation = isModerationNotification(item.type)
                const collabInvite = isBuildCollaboratorInvite(item.type)
                const policyPath = moderation ? parsePolicyPath(item.message) : null
                return (
                  <Box
                    key={item.id}
                    onClick={() => !moderation && !collabInvite && handleItemClick(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 12,
                      padding: '12px 8px',
                      borderBottom: '1px solid var(--gs-border)',
                      cursor: moderation || collabInvite ? 'default' : 'pointer',
                      borderRadius: 6,
                      ...(item.isRead ? {} : { background: 'rgba(232,93,4,0.04)' })
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        background: bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        fontWeight: 700,
                        color: '#fff',
                        fontFamily: 'DM Sans, sans-serif',
                        flexShrink: 0
                      }}
                    >
                      {item.actorInitials}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box component="div" sx={MESSAGE_LINE_SX}>
                        {item.actorUsername
                          ? item.message.split(new RegExp(`(${escapeRegExp(item.actorUsername)})`, 'gi')).map((part, i) =>
                            part.toLowerCase() === item.actorUsername.toLowerCase()
                              ? (
                                <Box key={i} component="span" sx={{ fontWeight: 700, color: '#E85D04' }}>
                                  {part}
                                </Box>
                                )
                              : (
                                <Box key={i} component="span" sx={{ fontWeight: 400 }}>
                                  {part}
                                </Box>
                                )
                          )
                          : item.message}
                      </Box>
                      <AppText size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 10, color: '#8B949E', marginTop: 2, lineHeight: 1.4 }}>
                        {format(new Date(item.createdAt), 'MMM d, h:mm a')}
                      </AppText>
                      {collabInvite
                        ? (
                        <Group spacing={8} mt={8} onClick={(e) => e.stopPropagation()}>
                          <AppButton
                            variant="filled"
                            size="xs"
                            compact
                            loading={inviteBusyId === item.id}
                            onClick={(e) => void handleAcceptInvite(item, e)}
                            sx={{ background: 'var(--gs-accent)' }}
                          >
                            Accept
                          </AppButton>
                          <AppButton
                            variant="default"
                            size="xs"
                            compact
                            disabled={inviteBusyId === item.id}
                            onClick={(e) => void handleDeclineInvite(item, e)}
                          >
                            Decline
                          </AppButton>
                          {getNavigatePath(item)
                            ? (
                            <AppButton
                              variant="subtle"
                              size="xs"
                              compact
                              onClick={() => handleItemClick(item)}
                            >
                              View build
                            </AppButton>
                              )
                            : null}
                        </Group>
                          )
                        : moderation
                        ? (
                        <Group spacing={8} mt={8} onClick={(e) => e.stopPropagation()}>
                          {policyPath
                            ? (
                            <Anchor component={Link} to={policyPath} size="xs">
                              Learn more
                            </Anchor>
                              )
                            : null}
                          <AppButton
                            variant="subtle"
                            size="xs"
                            compact
                            onClick={() => {
                              setAppealSummary(item.message)
                              setAppealOpen(true)
                            }}
                          >
                            Appeal
                          </AppButton>
                          {getNavigatePath(item)
                            ? (
                            <AppButton
                              variant="subtle"
                              size="xs"
                              compact
                              onClick={() => handleItemClick(item)}
                            >
                              View content
                            </AppButton>
                              )
                            : item.type === 'FlagResolved_Suspended'
                              ? (
                            <Anchor component={Link} to="/settings" size="xs">
                              Account actions
                            </Anchor>
                                )
                              : null}
                        </Group>
                          )
                        : null}
                    </Box>
                    <Box
                      sx={{
                        width: 11,
                        minHeight: 36,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      aria-hidden
                    >
                      {!item.isRead
                        ? (
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            background: '#E85D04',
                            flexShrink: 0
                          }}
                        />
                          )
                        : null}
                    </Box>
                  </Box>
                )
              })}
            </Box>
          ))}

          {notificationStore.hasMore && (
            <AppButton
              variant="outline"
              mt="lg"
              loaderPosition="right"
              sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)', minWidth: 148 }}
              onClick={() =>
                notificationStore.loadNotifications({
                  pageNumber: notificationStore.pageNumber + 1,
                  pageSize: 20
                })
              }
              loading={notificationStore.loading}
            >
              Load more
            </AppButton>
          )}
        </>
            )}

      <AppealComingSoonModal
        opened={appealOpen}
        onClose={() => setAppealOpen(false)}
        summary={appealSummary}
      />
    </Box>
  )
})

function escapeRegExp (s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
